'use client'

import { useAtomValue } from 'jotai'
import { useMemo } from 'react'

import {
  buildInstrumentChoiceData,
  buildPositionChoiceData,
  findChoicePosition,
  type InstrumentChoiceData,
  type PositionChoiceData,
} from '@/features/order/choice-picker/lib/choice-data'
import { useThrottledState } from '@/hooks/use-throttled-state'
import { toExchangeEnum, tradeVenueToExchangeKey } from '@/lib/proto/util'
import { useUserAccounts } from '@/service/account.hook'
import { useInstrumentList } from '@/service/instrument.hook'
import { useMultiIncrementalInstrumentStream } from '@/service/ws/use-incremental-instrument'
import { useMultiTickerStream } from '@/service/ws/use-ticker-stream'
import { allPositionsAtom } from '@/state/account'
import type { Account } from '@/types/generated/common/account'
import {
  type IncrementalTicker,
  InstrumentKind,
  MarketDataType,
  type Ticker,
} from '@/types/generated/common/types'

const TICK_THROTTLE_MS = 500

/** Keep the fields a partial incremental update leaves out. */
function mergeIncremental(prev: IncrementalTicker | undefined, next: IncrementalTicker) {
  if (!prev) return next
  const merged = { ...prev }
  for (const [key, value] of Object.entries(next)) {
    if (value !== undefined) (merged as Record<string, unknown>)[key] = value
  }
  merged.greeks = next.greeks ? { ...prev.greeks, ...next.greeks } : prev.greeks
  return merged
}

/**
 * Live card data for suggested instrument symbols on the account the question was asked
 * under: its venue's catalog (kind, option terms), the ticker (price, 24h, volume, funding)
 * and, for options, the incremental ticker (mark, IV, greeks). Null until the catalog loads.
 */
export function useInstrumentChoiceData(
  symbols: string[],
  account: Account | null | undefined,
  enabled: boolean
): Record<string, InstrumentChoiceData> | null {
  const exchange = account ? toExchangeEnum(tradeVenueToExchangeKey(account.tradeVenue)) : undefined
  const { data, isLoading } = useInstrumentList(exchange!, enabled && exchange !== undefined)
  const instruments = useMemo(() => {
    const bySymbol = new Map((data?.instruments ?? []).map((i) => [i.symbol, i]))
    return new Map(symbols.map((symbol) => [symbol, bySymbol.get(symbol)]))
  }, [data, symbols])

  const { state: tickers, setState: setTickers } = useThrottledState<Record<string, Ticker>>(
    {},
    TICK_THROTTLE_MS
  )
  const { state: incremental, setState: setIncremental } = useThrottledState<
    Record<string, IncrementalTicker>
  >({}, TICK_THROTTLE_MS)

  const tickerPayloads = useMemo(
    () =>
      exchange === undefined || !enabled
        ? []
        : symbols.map((symbol) => ({
            symbol,
            exchange,
            dataType: MarketDataType.MARKET_DATA_TYPE_TICKER,
          })),
    [enabled, exchange, symbols]
  )
  const optionPayloads = useMemo(
    () =>
      exchange === undefined || !enabled
        ? []
        : symbols
            .filter((s) => instruments.get(s)?.kind === InstrumentKind.INSTRUMENT_KIND_OPTION)
            .map((symbol) => ({
              symbol,
              exchange,
              dataType: MarketDataType.MARKET_DATA_TYPE_INCREMENTAL_TICKER,
            })),
    [enabled, exchange, instruments, symbols]
  )

  useMultiTickerStream({
    connected: tickerPayloads.length > 0,
    payloads: tickerPayloads,
    onMessage: (message) => {
      if (!message?.tickers?.length) return
      setTickers((prev) => {
        const next = { ...prev }
        for (const ticker of message.tickers) next[ticker.symbol] = ticker
        return next
      })
    },
  })
  useMultiIncrementalInstrumentStream({
    connected: optionPayloads.length > 0,
    payloads: optionPayloads,
    onMessage: (message) => {
      if (!message?.incrementalTickers?.length) return
      setIncremental((prev) => {
        const next = { ...prev }
        for (const ticker of message.incrementalTickers) {
          if (ticker.symbol) next[ticker.symbol] = mergeIncremental(prev[ticker.symbol], ticker)
        }
        return next
      })
    },
  })

  return useMemo(() => {
    if (!enabled || (isLoading && !data)) return null
    return Object.fromEntries(
      symbols.map((symbol) => [
        symbol,
        buildInstrumentChoiceData(
          symbol,
          instruments.get(symbol),
          tickers[symbol],
          incremental[symbol]
        ),
      ])
    )
  }, [data, enabled, incremental, instruments, isLoading, symbols, tickers])
}

/**
 * Card data for suggested position symbols, from the live cross-account positions. Rows on the
 * account the question was asked under win a tie. A symbol with no open position is left out.
 */
export function usePositionChoiceData(
  symbols: string[],
  accountId: string | undefined,
  enabled: boolean
): Record<string, PositionChoiceData> {
  const positions = useAtomValue(allPositionsAtom)
  const { data: accountsData } = useUserAccounts({ showDisabled: true })
  return useMemo(() => {
    if (!enabled) return {}
    const accounts = accountsData?.accounts ?? []
    const entries = symbols.flatMap((symbol) => {
      const position = findChoicePosition(positions, symbol, accountId)
      if (!position) return []
      const account = accounts.find((a) => a.accountId === position.accountId)
      return [[symbol, buildPositionChoiceData(position, account)] as const]
    })
    return Object.fromEntries(entries)
  }, [accountId, accountsData?.accounts, enabled, positions, symbols])
}
